import * as React from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowLeft,
  Check,
  GalleryHorizontal,
  Image as ImageIcon,
  Move,
  QrCode,
  Upload,
  Video,
} from 'lucide-react';
import {
  MUSIC_TRACKS,
  feedBrandToTemplateBrand,
  type ListingListResponse,
  type UploadedFontFace,
  musicTrackById,
} from '@proptr/shared';
import { useAuth } from '@proptr/api-client/react';
import {
  BANNER_CANVAS,
  type BannerRatio,
  DEFAULT_BRAND,
  ListingBanner,
  MARK_DEFAULT,
  MARK_MAX,
  MARK_MIN,
  QR_DEFAULT,
  QR_MAX,
  QR_MIN,
  bannerQrDefault,
  bannerHeight,
  bannerHeroHeight,
} from '@proptr/creative-templates';
import {
  LOOKBOOK_DEFAULT_BRAND,
  ListingLookbook,
} from '@proptr/creative-templates';
import { PLACEMENTS, type Placement } from '../lib/placements.js';
import {
  FontField,
  brandFontFamily,
  useUploadedFonts,
} from '../components/FontField.js';
import { SectionCard } from '../components/SectionCard.js';
import { AppShell } from '../components/layout/AppShell.js';
import { Button } from '../components/ui/button.js';
import { Card } from '../components/ui/card.js';
import { ColorField } from '../components/ui/color-field.js';
import { Input } from '../components/ui/input.js';
import { Label } from '../components/ui/label.js';
import { Select, type SelectOption } from '../components/ui/select.js';
import { Slider } from '../components/ui/slider.js';
import { Switch } from '../components/ui/switch.js';
import { Tooltip } from '../components/ui/tooltip.js';
import { Player } from '@remotion/player';
import {
  FPS,
  ListingVideo,
  photosFor,
  videoDurationFrames,
  videoSize,
} from '@proptr/video';
import { bannerListingFrom } from '../lib/bannerListing.js';
import { qrFromPointer, qrHandleStyle } from '../lib/qrDrag.js';
import {
  TEMPLATE_LOOKBOOK,
  TEMPLATE_OPTIONS,
  templateSupportsRatio,
} from '../lib/creativeTemplates.js';
import { isTestMode } from '../lib/testMode.js';
import { uploadImage } from '../lib/uploadImage.js';
import { cn } from '../lib/utils.js';
import { client } from '../main.js';

// Compose one listing into a set of social posts.
//
// Same split as the feed editor — settings left, live preview right —
// because it answers the same question: what will this actually look like
// when it lands. The difference is that a post has FOUR possible looks
// (two channels × two surfaces) and they disagree with each other, so the
// preview is tabbed rather than a single column.
//
// The creative itself is the existing listing banner: 1:1 for feed, 9:16
// for stories. Nothing here re-renders a listing from scratch.

type ListingRow = ListingListResponse['items'][number];

/** A Facebook Page from the Meta connection, with its linked Instagram. */
interface MetaPage {
  id: string;
  name: string;
  picture: string | null;
  instagram: {
    id: string;
    username: string | null;
    picture: string | null;
  } | null;
}

interface PostForm {
  /** Which Facebook Page publishes this, and which Instagram account
   *  goes with it. The Instagram account is not independently chosen —
   *  on Meta's model an IG professional account hangs off a Page — so
   *  picking a Page determines what the second dropdown can offer. */
  pageId: string | null;
  instagramId: string | null;
  /** What kind of creative this post carries. */
  format: PostFormat;
  listingId: string | null;
  template: string;
  placements: Placement[];
  caption: string;
  /** Whether the caption still tracks the listing, or the user edited it. */
  captionDirty: boolean;
  /** Post-scoped logo override. null = inherit the agency logo. */
  logoUrl: string | null;
  showLogo: boolean;
  logoSize: number;
  showQr: boolean;
  qrSize: number;
  mainColor: string;
  secondaryColor: string;
  mainFont: string;
  secondaryFont: string;
  /** Uploaded typefaces, which win over the stacks above. */
  mainFontFace: UploadedFontFace | null;
  secondaryFontFace: UploadedFontFace | null;
  /** QR placement per ratio, normalised against that ratio's photo area. */
  qrPosition: { x: number; y: number } | null;
  qrPositionVertical: { x: number; y: number } | null;
  schedule: 'now' | 'later';
  scheduledAt: string;
  /** Soundtrack id from the shared catalogue. null = silent. */
  musicId: string | null;
  /** An uploaded track, which wins over the catalogue pick when set. */
  musicUpload: { url: string; name: string } | null;
}

const EMPTY_FORM: PostForm = {
  pageId: null,
  instagramId: null,
  format: 'image',
  listingId: null,
  template: 'listing-banner',
  // Feed on both channels: the common case, and the two surfaces a
  // brokerage is most likely to already have set up.
  placements: ['facebook-feed', 'instagram-feed'],
  caption: '',
  captionDirty: false,
  logoUrl: null,
  showLogo: true,
  logoSize: MARK_DEFAULT,
  // On by default — Dubai requires the permit QR on advertised creative.
  showQr: true,
  qrSize: QR_DEFAULT,
  mainColor: '#6C5CE7',
  secondaryColor: '#0F172A',
  mainFont: "'Jost', system-ui, sans-serif",
  secondaryFont: "'Geist Mono', ui-monospace, monospace",
  mainFontFace: null,
  secondaryFontFace: null,
  qrPosition: null,
  qrPositionVertical: null,
  schedule: 'now',
  scheduledAt: '',
  musicId: null,
  musicUpload: null,
};

type PostFormat = 'image' | 'video' | 'carousel';

const FORMAT_OPTIONS: {
  id: PostFormat;
  label: string;
  description: string;
  Icon: typeof ImageIcon;
  /** Whether a creative pipeline exists for it yet. */
  ready: boolean;
}[] = [
  {
    id: 'image',
    label: 'Image',
    description: 'One still, built from the listing template.',
    Icon: ImageIcon,
    ready: true,
  },
  {
    id: 'video',
    label: 'Video',
    description: 'A short clip from the listing photos.',
    Icon: Video,
    ready: true,
  },
  {
    id: 'carousel',
    label: 'Carousel',
    description: 'Several cards people swipe through.',
    Icon: GalleryHorizontal,
    ready: false,
  },
];

const SCHEDULE_OPTIONS: SelectOption<'now' | 'later'>[] = [
  { value: 'now', label: 'Publish now' },
  { value: 'later', label: 'Schedule for later' },
];

/**
 * Default caption from the listing.
 *
 * Regenerated whenever the listing changes UNTIL the user types, after
 * which it is theirs — silently overwriting an edited caption because a
 * different listing was picked is the kind of thing that loses work.
 */
function captionFor(l: ListingRow | null): string {
  if (!l) return '';
  const place = [l.area, l.city].filter(Boolean).join(', ');
  const price =
    typeof l.price === 'number'
      ? new Intl.NumberFormat('en-AE', {
          style: 'currency',
          currency: l.currency || 'AED',
          currencyDisplay: 'code',
          maximumFractionDigits: 0,
        }).format(l.price)
      : null;
  const specs = [
    l.bedrooms == null ? null : `${l.bedrooms} bed`,
    l.bathrooms == null ? null : `${l.bathrooms} bath`,
  ]
    .filter(Boolean)
    .join(' · ');
  return [
    l.title || (place ? `New in ${place}` : 'New listing'),
    [specs, place].filter(Boolean).join(' — '),
    price ? `${price}` : null,
    'DM us for a viewing.',
  ]
    .filter(Boolean)
    .join('\n\n');
}

export function PostEditPage() {
  const { enterprise } = useAuth();
  const [form, setForm] = React.useState<PostForm>(EMPTY_FORM);
  // Pages come from the Meta connection, so they're fetched rather than
  // configured. Null while loading, [] when there's no connection or no
  // Page assets were granted — the two read differently to the user.
  const [pages, setPages] = React.useState<MetaPage[] | null>(null);
  // Why the list is empty, which is not one situation but two: no Meta
  // connection at all, versus connected with no Page assets shared. They
  // have completely different fixes, and telling a connected user to
  // "connect Meta first" sends them to re-do something already done.
  const [pagesState, setPagesState] = React.useState<
    'loading' | 'ok' | 'disconnected'
  >('loading');

  React.useEffect(() => {
    let alive = true;
    void (async () => {
      try {
        const res = await fetch('/api/auth/meta/pages', {
          credentials: 'include',
        });
        if (!alive) return;
        if (!res.ok) {
          // 409 is the API's "no Meta connection" — a state to explain,
          // not an error to shout about.
          setPages([]);
          setPagesState(res.status === 409 ? 'disconnected' : 'ok');
          return;
        }
        const body = (await res.json()) as { items: MetaPage[] };
        setPages(body.items);
        setPagesState('ok');
        // One Page is not a choice worth making anyone click through.
        if (body.items.length === 1) {
          const only = body.items[0]!;
          setForm((f) =>
            f.pageId
              ? f
              : {
                  ...f,
                  pageId: only.id,
                  instagramId: only.instagram?.id ?? null,
                },
          );
        }
      } catch {
        if (alive) {
          setPages([]);
          setPagesState('ok');
        }
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  const selectedPage = pages?.find((p) => p.id === form.pageId) ?? null;

  // Changing Page invalidates the Instagram choice: the account belongs
  // to the Page, so carrying it across would post to an account the new
  // Page has nothing to do with.
  const selectPage = (pageId: string) => {
    const page = pages?.find((p) => p.id === pageId) ?? null;
    setForm((f) => ({
      ...f,
      pageId,
      instagramId: page?.instagram?.id ?? null,
    }));
  };
  const set = <K extends keyof PostForm>(key: K, value: PostForm[K]) =>
    setForm((f) => ({ ...f, [key]: value }));

  const [logoBusy, setLogoBusy] = React.useState(false);
  const [logoError, setLogoError] = React.useState<string | null>(null);
  const logoInputRef = React.useRef<HTMLInputElement>(null);

  const handleLogoFile = async (file: File) => {
    setLogoBusy(true);
    setLogoError(null);
    try {
      set('logoUrl', await uploadImage({ file, kind: 'agency-logo' }));
    } catch (err) {
      setLogoError((err as Error).message);
    } finally {
      setLogoBusy(false);
    }
  };

  // Stable for the life of this editor, so a face uploaded before the post
  // is ever saved still gets a unique family. See brandFontFamily.
  const sessionToken = React.useMemo(
    () => Math.random().toString(36).slice(2, 10),
    [],
  );

  const [musicBusy, setMusicBusy] = React.useState(false);
  const [musicError, setMusicError] = React.useState<string | null>(null);
  const musicInputRef = React.useRef<HTMLInputElement>(null);

  const handleMusicFile = async (file: File) => {
    setMusicBusy(true);
    setMusicError(null);
    try {
      const url = await uploadImage({ file, kind: 'brand-audio' });
      set('musicUpload', { url, name: file.name });
    } catch (err) {
      setMusicError((err as Error).message);
    } finally {
      setMusicBusy(false);
    }
  };

  const handleFontFile = async (slot: 'main' | 'secondary', file: File) => {
    const key = slot === 'main' ? 'mainFontFace' : 'secondaryFontFace';
    const url = await uploadImage({ file, kind: 'brand-font' });
    set(key, {
      family: brandFontFamily(
        'post',
        null,
        sessionToken,
        slot === 'main' ? 'display' : 'mono',
      ),
      url,
    });
  };

  const [listings, setListings] = React.useState<ListingRow[] | null>(null);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    let alive = true;
    client.listings
      .list({ status: 'active', type: 'all', page: 1, limit: 200 })
      .then((res) => {
        if (!alive) return;
        setListings(res.items);
        // Land on something rather than an empty canvas.
        setForm((f) =>
          f.listingId
            ? f
            : {
                ...f,
                listingId: res.items[0]?._id ?? null,
                caption: f.captionDirty ? f.caption : captionFor(res.items[0] ?? null),
              },
        );
      })
      .catch((err: Error) => {
        if (alive) setError(err.message);
      });
    return () => {
      alive = false;
    };
  }, []);

  const listing =
    listings?.find((l) => l._id === form.listingId) ?? null;

  const listingOptions: SelectOption<string>[] = (listings ?? []).map((l) => ({
    value: l._id,
    label: [l.title || 'Untitled listing', l.area].filter(Boolean).join(' · '),
  }));

  const pickListing = (id: string) => {
    const next = listings?.find((l) => l._id === id) ?? null;
    setForm((f) => ({
      ...f,
      listingId: id,
      caption: f.captionDirty ? f.caption : captionFor(next),
    }));
  };

  const togglePlacement = (id: Placement) =>
    setForm((f) => ({
      ...f,
      placements: f.placements.includes(id)
        ? f.placements.filter((p) => p !== id)
        : // Keep the canonical order so the preview tabs don't reshuffle
          // as placements are toggled on and off.
          PLACEMENTS.filter((p) => p.id === id || f.placements.includes(p.id)).map(
            (p) => p.id,
          ),
    }));

  // Switching to a square-only template must not leave a story placement
  // selected — it would preview and publish the wrong canvas.
  React.useEffect(() => {
    setForm((f) => {
      const kept = f.placements.filter((id) => {
        const p = PLACEMENTS.find((x) => x.id === id);
        return !p || templateSupportsRatio(f.template, p.ratio);
      });
      return kept.length === f.placements.length ? f : { ...f, placements: kept };
    });
  }, [form.template]);

  useUploadedFonts([form.mainFontFace, form.secondaryFontFace]);

  const agencyLogo = enterprise?.logo ?? null;
  // Feed-style override: the post's own mark wins, else the agency's.
  const effectiveLogo = form.logoUrl ?? agencyLogo;

  const canPublish =
    !!form.listingId &&
    form.placements.length > 0 &&
    (form.schedule === 'now' || !!form.scheduledAt);

  return (
    <AppShell>
      <div className="space-y-6 pb-10">
        <div className="min-w-0">
          <h1 className="font-display text-[40px] font-medium leading-none tracking-[-0.02em]">
            Create post
          </h1>
          <div className="mt-3 flex items-center gap-3">
            <Link
              to="/marketing"
              className="inline-flex items-center gap-1.5 font-mono text-[10px] font-semibold uppercase tracking-[0.08em] text-muted-foreground transition-colors hover:text-foreground"
            >
              <ArrowLeft className="h-3 w-3" />
              Back to marketing
            </Link>
            <span className="text-muted-foreground/50">·</span>
            <div className="eyebrow">Marketing / Post</div>
          </div>
        </div>

        {error && (
          <Card className="border-destructive/40 bg-destructive/10 p-4 font-mono text-[11px] text-destructive">
            {error}
          </Card>
        )}

        <div className="grid gap-6 lg:grid-cols-2">
          {/* Settings */}
          <div className="space-y-4">
            {/* One card, always open. The fields are short and read as a
                single sequence — what you're posting, where it goes, and
                when — so four collapsibles was four things to click past
                rather than four things worth hiding. */}
            <SectionCard
              title="Compose"
              subtitle="What you're posting, where it goes, and when"
              icon={ImageIcon}
              collapsible={false}
            >
              <div className="grid grid-cols-12 gap-4 p-5">
                {/* Account first: everything below is scoped to it. The
                    Instagram list is derived from the Page rather than
                    fetched separately — Meta hangs an IG professional
                    account off a Page, so there is no independent list to
                    offer. */}
                <div className="col-span-12 sm:col-span-6">
                  <Label>Facebook page</Label>
                  <Select
                    className="mt-1 bg-muted/30"
                    options={(pages ?? []).map((p) => ({
                      value: p.id,
                      label: p.name,
                      image: p.picture,
                    }))}
                    value={form.pageId ?? ''}
                    onChange={(v) => selectPage(v)}
                    placeholder={
                      pagesState === 'loading'
                        ? 'Loading…'
                        : pagesState === 'disconnected'
                          ? 'Connect Meta first'
                          : (pages?.length ?? 0) === 0
                            ? 'No pages shared'
                            : 'Select a page'
                    }
                  />
                  {pagesState === 'ok' && (pages?.length ?? 0) === 0 && (
                    // Connected but empty-handed. The fix is in Meta's
                    // login configuration, not in this app, so say so
                    // rather than implying the connection failed.
                    <p className="mt-1.5 font-mono text-[10px] leading-relaxed text-muted-foreground">
                      Meta is connected but shared no pages. Add Pages and
                      Instagram accounts to the app's login configuration,
                      then reconnect from Marketing.
                    </p>
                  )}
                </div>
                <div className="col-span-12 sm:col-span-6">
                  <Label>Instagram account</Label>
                  <Select
                    className="mt-1 bg-muted/30"
                    options={
                      selectedPage?.instagram
                        ? [
                            {
                              value: selectedPage.instagram.id,
                              label: selectedPage.instagram.username
                                ? `@${selectedPage.instagram.username}`
                                : selectedPage.instagram.id,
                              image: selectedPage.instagram.picture,
                            },
                          ]
                        : []
                    }
                    value={form.instagramId ?? ''}
                    onChange={(v) => set('instagramId', v)}
                    placeholder={
                      !form.pageId
                        ? 'Pick a page first'
                        : selectedPage?.instagram
                          ? 'Select an account'
                          : 'No Instagram linked to this page'
                    }
                  />
                  {form.pageId && !selectedPage?.instagram && (
                    // Worth saying why rather than leaving an empty
                    // dropdown: the fix is in Meta, not here.
                    <p className="mt-1.5 font-mono text-[10px] leading-relaxed text-muted-foreground">
                      Link an Instagram professional account to this page in
                      Meta to post there.
                    </p>
                  )}
                </div>

                <div className="col-span-12">
                  <div className="-mx-5 border-t border-[hsl(var(--hairline))]" />
                </div>

                {/* Placements first: which surfaces a post goes to
                    decides which ratios it needs, and the format picker
                    below is the answer to that question — asking it in the
                    other order made people pick a format, then discover a
                    surface it can't fill.

                    The two channels sit side by side rather than stacked so
                    all four surfaces read as one decision on one row. */}
                <div className="col-span-12">
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    {(['Facebook', 'Instagram'] as const).map((channel) => (
                      <div key={channel}>
                        <div className="mono-label text-muted-foreground">
                          {channel}
                        </div>
                        <div className="mt-2 grid grid-cols-2 gap-2">
                          {PLACEMENTS.filter((p) => p.channel === channel).map(
                            (p) => {
                              const supported = templateSupportsRatio(
                                form.template,
                                p.ratio,
                              );
                              const on =
                                supported && form.placements.includes(p.id);
                              return (
                                <div
                                  key={p.id}
                                  className={cn(
                                    'flex h-11 items-center justify-between gap-2 rounded-md border border-border bg-muted/30 px-3 dark:border-white/10',
                                    !supported && 'opacity-40',
                                  )}
                                >
                                  {/* Surface only. The ratio is implied by
                                      it — Feed is square, Story is
                                      vertical — and printing it made four
                                      switches read as eight facts. It still
                                      reaches screen readers via the
                                      Switch's aria-label. */}
                                  <Label
                                    htmlFor={`placement-${p.id}`}
                                    className="mb-0 min-w-0 truncate"
                                  >
                                    {p.surface}
                                  </Label>
                                  <Switch
                                    id={`placement-${p.id}`}
                                    checked={on}
                                    disabled={!supported}
                                    onCheckedChange={() => togglePlacement(p.id)}
                                    aria-label={`${channel} ${p.surface} (${p.ratio})`}
                                  />
                                </div>
                              );
                            },
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                  {form.placements.length === 0 && (
                    <p className="mt-2 font-mono text-[10px] text-destructive">
                      Pick at least one surface — a post with no placement has
                      nowhere to go.
                    </p>
                  )}
                </div>

                <div className="col-span-12">
                  <div className="-mx-5 border-t border-[hsl(var(--hairline))]" />
                </div>
                <div className="col-span-12">
                  <Label>Format</Label>
                  <div
                    role="radiogroup"
                    aria-label="Post format"
                    className="mt-1 grid grid-cols-1 gap-2 sm:grid-cols-3"
                  >
                    {FORMAT_OPTIONS.map(({ id, label, description, Icon, ready }) => {
                      const on = form.format === id;
                      return (
                        <button
                          key={id}
                          type="button"
                          role="radio"
                          aria-checked={on}
                          onClick={() => set('format', id)}
                          className={cn(
                            'group relative rounded-lg border p-3 text-left transition-colors',
                            on
                              ? 'border-primary bg-primary/5 ring-1 ring-primary/25'
                              : 'border-border bg-card hover:border-primary/40 hover:bg-muted dark:border-white/10',
                          )}
                        >
                          <span
                            className={cn(
                              'flex h-8 w-8 items-center justify-center rounded-full transition-colors',
                              on
                                ? 'bg-primary/15 text-primary'
                                : 'bg-muted text-foreground group-hover:bg-primary/10 group-hover:text-primary',
                            )}
                          >
                            <Icon className="h-4 w-4" />
                          </span>
                          <span className="mt-2 block font-sans text-[13px] font-semibold leading-tight">
                            {label}
                          </span>
                          <span className="mt-1 block font-sans text-[11px] leading-snug text-muted-foreground">
                            {description}
                          </span>
                          {!ready && (
                            <span className="mt-1.5 block font-mono text-[9px] uppercase tracking-[0.06em] text-muted-foreground/70">
                              Not built yet
                            </span>
                          )}
                          {on && (
                            <span
                              aria-hidden
                              className="absolute right-2 top-2 flex h-4 w-4 items-center justify-center rounded-full bg-primary text-primary-foreground"
                            >
                              <Check className="h-2.5 w-2.5" />
                            </span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div className="col-span-12">
                  <div className="-mx-5 border-t border-[hsl(var(--hairline))]" />
                </div>

                {form.format === 'video' && (
                  <div className="col-span-12">
                    <Label>Music</Label>
                    {form.musicUpload ? (
                      <div className="mt-1 flex h-11 items-center gap-2 rounded-md border border-border bg-muted/30 px-3.5 dark:border-white/10">
                        <span className="min-w-0 flex-1 truncate font-mono text-[12px] text-foreground/80">
                          {form.musicUpload.name}
                        </span>
                        <button
                          type="button"
                          onClick={() => set('musicUpload', null)}
                          className="shrink-0 font-mono text-[10px] uppercase tracking-[0.06em] text-muted-foreground transition-colors hover:text-destructive"
                        >
                          Remove
                        </button>
                      </div>
                    ) : MUSIC_TRACKS.length === 0 ? (
                      // No dropdown when there is nothing to drop down. A
                      // select whose only option is "No music" reads as a
                      // list that failed to load rather than as an empty
                      // one, which is exactly how it got read.
                      <div className="mt-1 flex h-11 items-center gap-3 rounded-md border border-dashed border-[hsl(var(--hairline))] px-3.5">
                        <span className="min-w-0 flex-1 truncate font-mono text-[11px] text-muted-foreground">
                          No tracks in the library yet
                        </span>
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          className="shrink-0"
                          disabled={musicBusy}
                          onClick={() => musicInputRef.current?.click()}
                        >
                          <Upload />
                          {musicBusy ? 'Uploading…' : 'Upload a track'}
                        </Button>
                      </div>
                    ) : (
                      <div className="mt-1 flex items-center gap-2">
                        <Select
                          className="min-w-0 flex-1 bg-muted/30"
                          options={[
                            { value: '', label: 'No music' },
                            ...MUSIC_TRACKS.map((t) => ({
                              value: t.id,
                              label: `${t.name} · ${t.mood}`,
                            })),
                          ]}
                          value={form.musicId ?? ''}
                          onChange={(v) => set('musicId', v || null)}
                          searchable={MUSIC_TRACKS.length > 8}
                        />
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          className="h-11 shrink-0"
                          disabled={musicBusy}
                          onClick={() => musicInputRef.current?.click()}
                        >
                          <Upload />
                          {musicBusy ? 'Uploading…' : 'Upload'}
                        </Button>
                      </div>
                    )}
                    <input
                      ref={musicInputRef}
                      type="file"
                      accept="audio/mpeg,audio/mp4,audio/wav,.mp3,.m4a,.wav"
                      className="hidden"
                      onChange={(e) => {
                        const f = e.target.files?.[0];
                        if (f) void handleMusicFile(f);
                        e.target.value = '';
                      }}
                    />
                    <p className="mt-1.5 font-mono text-[10px] leading-relaxed text-muted-foreground">
                      {musicError ? (
                        <span className="text-destructive">{musicError}</span>
                      ) : MUSIC_TRACKS.length === 0 ? (
                        <>
                          No curated tracks yet — upload an MP3, or download
                          one from Meta Sound Collection
                          (facebook.com/sound/collection) and add it to the
                          shared catalogue.
                        </>
                      ) : (
                        'Royalty-free from Meta Sound Collection, cleared for both organic posts and ads.'
                      )}
                    </p>
                  </div>
                )}

                <div className="col-span-12">
                  <Label>Listing</Label>
                  <Select
                    className="mt-1 bg-muted/30"
                    options={listingOptions}
                    value={form.listingId ?? ''}
                    onChange={pickListing}
                    searchable
                  />
                  <p className="mt-1.5 font-mono text-[10px] leading-relaxed text-muted-foreground">
                    {listings === null
                      ? 'Loading your listings…'
                      : `${listings.length} active listing${listings.length === 1 ? '' : 's'} to choose from.`}
                  </p>
                </div>

                <div className="col-span-12">
                  <Label>Template</Label>
                  <Select
                    className="mt-1 bg-muted/30"
                    options={TEMPLATE_OPTIONS}
                    value={form.template}
                    onChange={(v) => set('template', v)}
                    searchable={false}
                  />
                  <p className="mt-1.5 font-mono text-[10px] leading-relaxed text-muted-foreground">
                    Same templates as the feed builder. Post versions
                    render identically for now — they exist so the two can
                    diverge without touching the catalog.
                    {form.template === TEMPLATE_LOOKBOOK &&
                      ' The lookbook is square only, so story surfaces are unavailable.'}
                  </p>
                </div>

                <div className="col-span-12 sm:col-span-6">
                  <Label>Logo</Label>
                  <div className="mt-1 flex items-center gap-3">
                    <div className="flex h-14 w-14 shrink-0 items-center justify-center overflow-hidden rounded-md border border-border bg-muted/30">
                      {effectiveLogo ? (
                        <img
                          src={effectiveLogo}
                          alt=""
                          className="h-full w-full object-contain p-1.5"
                        />
                      ) : (
                        <ImageIcon className="h-4 w-4 text-muted-foreground" />
                      )}
                    </div>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      disabled={logoBusy}
                      onClick={() => logoInputRef.current?.click()}
                    >
                      <Upload />
                      {logoBusy ? 'Uploading…' : 'Replace'}
                    </Button>
                    {form.logoUrl && (
                      <button
                        type="button"
                        onClick={() => set('logoUrl', null)}
                        className="font-mono text-[10px] uppercase tracking-[0.06em] text-muted-foreground transition-colors hover:text-foreground"
                      >
                        Reset
                      </button>
                    )}
                    <input
                      ref={logoInputRef}
                      type="file"
                      accept="image/svg+xml,image/png,image/jpeg,image/webp"
                      className="hidden"
                      onChange={(e) => {
                        const f = e.target.files?.[0];
                        if (f) void handleLogoFile(f);
                        e.target.value = '';
                      }}
                    />
                  </div>
                  <p className="mt-1.5 min-h-[2rem] font-mono text-[10px] leading-relaxed text-muted-foreground">
                    {logoError ? (
                      <span className="text-destructive">{logoError}</span>
                    ) : form.logoUrl ? (
                      'Custom mark for this post only — your agency logo is unchanged. Reset to go back to it.'
                    ) : (
                      'Using your agency logo. Replace it to use a different mark for this post only.'
                    )}
                  </p>

                  <div className="mt-4 flex items-center justify-between gap-4">
                    <Label htmlFor="post-logo-enabled" className="mb-0">
                      Show logo
                    </Label>
                    <Switch
                      id="post-logo-enabled"
                      checked={form.showLogo}
                      onCheckedChange={(v) => set('showLogo', v)}
                      aria-label="Show logo on the creative"
                    />
                  </div>

                  <div className="mt-3">
                    <Label htmlFor="post-logo-size">Logo size</Label>
                    <Slider
                      id="post-logo-size"
                      className="mt-2"
                      min={MARK_MIN}
                      max={MARK_MAX}
                      step={4}
                      value={form.logoSize}
                      disabled={!form.showLogo}
                      onChange={(v) => set('logoSize', v)}
                      valueLabel={`${form.logoSize} px`}
                    />
                  </div>
                </div>

                <div className="col-span-12 sm:col-span-6 sm:border-l sm:border-[hsl(var(--hairline))] sm:pl-5">
                  <Label>Permit QR</Label>
                  <div className="mt-1 flex h-14 items-center">
                    <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-md border border-border bg-muted/30">
                      <QrCode className="h-5 w-5 text-muted-foreground" />
                    </div>
                  </div>
                  <p className="mt-1.5 min-h-[2rem] font-mono text-[10px] leading-relaxed text-muted-foreground">
                    Dubai requires the Trakheesi QR on advertised listings.
                    Only shown for listings that carry one.
                  </p>

                  <div className="mt-4 flex items-center justify-between gap-4">
                    <Label htmlFor="post-qr-enabled" className="mb-0">
                      Show permit QR
                    </Label>
                    <Switch
                      id="post-qr-enabled"
                      checked={form.showQr}
                      onCheckedChange={(v) => set('showQr', v)}
                      aria-label="Show the permit QR on the creative"
                    />
                  </div>

                  <div className="mt-3">
                    <Label htmlFor="post-qr-size">QR size</Label>
                    <Slider
                      id="post-qr-size"
                      className="mt-2"
                      min={QR_MIN}
                      max={QR_MAX}
                      step={4}
                      value={form.qrSize}
                      disabled={!form.showQr}
                      onChange={(v) => set('qrSize', v)}
                      valueLabel={`${form.qrSize} px`}
                    />
                  </div>
                </div>

                <div className="col-span-12">
                  <div className="-mx-5 border-t border-[hsl(var(--hairline))]" />
                </div>

                <div className="col-span-12 sm:col-span-6">
                  <ColorField
                    id="post-main-color"
                    label="Main color"
                    labelClassName=""
                    value={form.mainColor}
                    onChange={(v) => set('mainColor', v)}
                  />
                </div>
                <div className="col-span-12 sm:col-span-6">
                  <ColorField
                    id="post-secondary-color"
                    label="Secondary color"
                    labelClassName=""
                    value={form.secondaryColor}
                    onChange={(v) => set('secondaryColor', v)}
                  />
                  <p className="mt-1.5 font-mono text-[10px] leading-relaxed text-muted-foreground">
                    Tints the shade behind the price. Keep it dark — the
                    white type sits on top of it.
                  </p>
                </div>

                <div className="col-span-12 sm:col-span-6 sm:col-start-1">
                  <FontField
                    id="post-main-font"
                    label="Main font"
                    value={form.mainFont}
                    face={form.mainFontFace}
                    onChange={(v) => set('mainFont', v)}
                    onUpload={(file) => handleFontFile('main', file)}
                    onClear={() => set('mainFontFace', null)}
                  />
                </div>
                <div className="col-span-12 sm:col-span-6">
                  <FontField
                    id="post-secondary-font"
                    label="Secondary font"
                    value={form.secondaryFont}
                    face={form.secondaryFontFace}
                    onChange={(v) => set('secondaryFont', v)}
                    onUpload={(file) => handleFontFile('secondary', file)}
                    onClear={() => set('secondaryFontFace', null)}
                  />
                </div>

                <div className="col-span-12">
                  <div className="-mx-5 border-t border-[hsl(var(--hairline))]" />
                </div>
                <div className="col-span-12">
                <textarea
                  className="min-h-[140px] w-full resize-y rounded-md border border-border bg-muted/30 px-3.5 py-2.5 font-sans text-[13px] leading-snug outline-none transition-colors focus-visible:border-transparent focus-visible:ring-2 focus-visible:ring-ring dark:border-white/10"
                  value={form.caption}
                  placeholder="Write something about this listing…"
                  onChange={(e) =>
                    setForm((f) => ({
                      ...f,
                      caption: e.target.value,
                      captionDirty: true,
                    }))
                  }
                />
                <div className="mt-2 flex items-center justify-between gap-3">
                  <p className="font-mono text-[10px] leading-relaxed text-muted-foreground">
                    {form.captionDirty
                      ? 'Yours — changing the listing no longer rewrites it.'
                      : 'Generated from the listing. Editing it stops that.'}
                  </p>
                  <div className="flex shrink-0 items-center gap-2">
                    <span
                      className={cn(
                        'font-mono text-[10px] tabular-nums',
                        form.caption.length > 2200
                          ? 'text-destructive'
                          : 'text-muted-foreground',
                      )}
                    >
                      {form.caption.length}/2200
                    </span>
                    {form.captionDirty && (
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={() =>
                          setForm((f) => ({
                            ...f,
                            caption: captionFor(listing),
                            captionDirty: false,
                          }))
                        }
                      >
                        Reset
                      </Button>
                    )}
                  </div>
                </div>
                </div>
                <div className="col-span-12">
                  <div className="-mx-5 border-t border-[hsl(var(--hairline))]" />
                </div>
                <div className="col-span-12 sm:col-span-6">
                  <Label>Timing</Label>
                  <Select
                    className="mt-1 bg-muted/30"
                    options={SCHEDULE_OPTIONS}
                    value={form.schedule}
                    onChange={(v) => set('schedule', v)}
                    searchable={false}
                  />
                </div>
                {form.schedule === 'later' && (
                  <div className="col-span-12 sm:col-span-6">
                    <Label htmlFor="post-when">Date and time</Label>
                    <Input
                      id="post-when"
                      type="datetime-local"
                      className="mt-1 bg-muted/30"
                      value={form.scheduledAt}
                      onChange={(e) => set('scheduledAt', e.target.value)}
                    />
                  </div>
                )}
              </div>
            </SectionCard>

            <div className="flex items-center justify-end gap-3 pt-1">
              {isTestMode() ? (
                <Tooltip
                  label="Can't publish a post in test mode"
                  className="cursor-not-allowed"
                >
                  <Button size="sm" disabled>
                    Publish
                  </Button>
                </Tooltip>
              ) : (
                <Button size="sm" disabled={!canPublish}>
                  {form.schedule === 'later' ? 'Schedule post' : 'Publish'}
                </Button>
              )}
            </div>
          </div>

          {/* Preview */}
          <div className="lg:sticky lg:top-[calc(var(--header-h,4rem)+var(--banner-h,0px)+0.5rem)] lg:h-[calc(100dvh-var(--header-h,4rem)-var(--banner-h,0px)-1.5rem)]">
            <PostPreview
              form={form}
              listing={listing}
              avatarUrl={effectiveLogo}
              onQrPositionChange={(p, ratio) =>
                set(ratio === '9:16' ? 'qrPositionVertical' : 'qrPosition', p)
              }
            />
          </div>
        </div>
      </div>
    </AppShell>
  );
}

function PostPreview({
  form,
  listing,
  avatarUrl,
  onQrPositionChange,
}: {
  form: PostForm;
  listing: ListingRow | null;
  avatarUrl: string | null;
  onQrPositionChange: (
    p: { x: number; y: number } | null,
    ratio: BannerRatio,
  ) => void;
}) {
  // The creative on its own, at the ratios this post is actually built
  // for. Platform chrome was here and came out — it made the panel busy
  // without answering anything the creative doesn't answer by itself.
  const ratios = React.useMemo(() => {
    const wanted = PLACEMENTS.filter((p) =>
      form.placements.includes(p.id),
    ).map((p) => p.ratio);
    return (['1:1', '9:16'] as BannerRatio[]).filter((r) =>
      wanted.includes(r),
    );
  }, [form.placements]);

  const [active, setActive] = React.useState<BannerRatio>('1:1');
  const ratio = ratios.includes(active) ? active : (ratios[0] ?? null);

  // Each ratio carries its own placement — the square and the vertical
  // put the code in visibly different spots.
  const saved =
    ratio === '9:16' ? form.qrPositionVertical : form.qrPosition;

  const [editing, setEditing] = React.useState(false);
  const [draft, setDraft] = React.useState(saved);
  const cardRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => setDraft(saved), [saved, ratio]);
  React.useEffect(() => {
    if (!form.showQr) setEditing(false);
  }, [form.showQr]);

  const width = 340;
  // Preview-only rounding, in CANVAS px like every other template value —
  // the export is square-cornered. The Player takes CSS px instead, so it
  // has to be scaled down by the same factor the canvas is, or the video
  // comes out visibly rounder than the still beside it.
  const PREVIEW_RADIUS = 20;
  const cssRadius = PREVIEW_RADIUS * (width / BANNER_CANVAS);
  const isLookbook = form.template === TEMPLATE_LOOKBOOK;
  const galleryCount = (listing?.gallery ?? []).length;
  const qrGeo = {
    qrSize: form.qrSize,
    canvasH: isLookbook || !ratio ? 1080 : bannerHeight(ratio, 'social'),
    heroH:
      isLookbook || !ratio
        ? 1080
        : bannerHeroHeight(ratio, galleryCount, 'social'),
  };

  const startDrag = (e: React.PointerEvent) => {
    const card = cardRef.current;
    if (!card) return;
    e.preventDefault();
    const rect = card.getBoundingClientRect();
    const move = (ev: PointerEvent) =>
      setDraft(qrFromPointer(ev.clientX, ev.clientY, rect, qrGeo));
    const up = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
  };

  const brand = React.useMemo(
    () => ({
      ...DEFAULT_BRAND,
      // Same translation the feed builder and the export both use, so a
      // post's colours and faces can't drift from a feed's.
      ...feedBrandToTemplateBrand(
        {
          mainColor: form.mainColor,
          secondaryColor: form.secondaryColor,
          mainFont: form.mainFont,
          secondaryFont: form.secondaryFont,
          mainFontFace: form.mainFontFace,
          secondaryFontFace: form.secondaryFontFace,
        },
        { agencyLogo: avatarUrl, ratio: ratio ?? '1:1' },
      ),
      showLogo: form.showLogo,
      logoSize: form.logoSize,
      showQr: form.showQr,
      qrSize: form.qrSize,
      qrPosition: draft,
      radius: PREVIEW_RADIUS,
    }),
    [avatarUrl, ratio, form, draft, PREVIEW_RADIUS],
  );

  const dirty = JSON.stringify(draft) !== JSON.stringify(saved);

  return (
    <Card className="flex h-full min-h-[640px] flex-col overflow-hidden p-0 lg:min-h-0">
      <div className="flex shrink-0 items-center gap-2 border-b border-[hsl(var(--hairline))] px-5 py-3">
        {ratios.length === 0 ? (
          <span className="font-mono text-[10px] uppercase tracking-[0.06em] text-muted-foreground">
            No surfaces selected
          </span>
        ) : (
          <div className="flex h-8 items-center gap-0.5 rounded-md border border-[hsl(var(--hairline))] bg-muted/30 p-0.5">
            {ratios.map((r) => (
              <button
                key={r}
                type="button"
                onClick={() => setActive(r)}
                disabled={editing}
                className={cn(
                  'flex h-full items-center rounded-[4px] px-2.5 font-mono text-[10px] tabular-nums transition-colors disabled:opacity-50',
                  ratio === r
                    ? 'bg-background text-foreground shadow-sm'
                    : 'text-muted-foreground hover:text-foreground',
                )}
              >
                {r}
              </button>
            ))}
          </div>
        )}

        <div className="ml-auto flex items-center gap-2">
          {editing ? (
            <>
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() => {
                  setDraft(null);
                  if (ratio) onQrPositionChange(null, ratio);
                }}
              >
                Reset
              </Button>
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() => {
                  setDraft(saved);
                  setEditing(false);
                }}
              >
                Cancel
              </Button>
              <Button
                type="button"
                size="sm"
                disabled={!dirty}
                onClick={() => {
                  if (ratio) onQrPositionChange(draft, ratio);
                  setEditing(false);
                }}
              >
                Save
              </Button>
            </>
          ) : (
            form.showQr &&
            !!ratio &&
            !isLookbook &&
            form.format === 'image' && (
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() => setEditing(true)}
              >
                <Move />
                Move QR code
              </Button>
            )
          )}
        </div>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto p-5">
        {form.format === 'carousel' ? (
          // Still no carousel pipeline; showing the single creative here
          // would claim one exists.
          <p className="py-16 text-center font-mono text-[11px] leading-relaxed text-muted-foreground">
            No preview — the carousel creative isn't built yet.
          </p>
        ) : form.format === 'video' && listing && ratio ? (
          // The Remotion Player mounts the same composition the API
          // renders headlessly, so what plays here is what ships.
          <div className="flex justify-center">
            <Player
              component={ListingVideo}
              inputProps={{
                brand,
                listing: bannerListingFrom(listing),
                ratio,
                music: form.musicUpload
                  ? { url: form.musicUpload.url }
                  : (() => {
                      const t = musicTrackById(form.musicId);
                      return t ? { url: t.url } : null;
                    })(),
              }}
              // Same length the API will render: the square cycles up to
              // four photos, the vertical shows its gallery in the rail
              // instead and stays on one.
              durationInFrames={videoDurationFrames(
                photosFor(bannerListingFrom(listing), ratio).length,
                FPS,
              )}
              fps={FPS}
              compositionWidth={videoSize(ratio).width}
              compositionHeight={videoSize(ratio).height}
              style={{ width, borderRadius: cssRadius, overflow: 'hidden' }}
              controls
              loop
            />
          </div>
        ) : !listing || !ratio ? (
          <p className="py-16 text-center font-mono text-[11px] text-muted-foreground">
            {listing ? 'Pick a surface to preview.' : 'Pick a listing to preview.'}
          </p>
        ) : (
          <div className="flex justify-center">
            <div
              ref={cardRef}
              className={cn(
                'relative',
                editing && 'rounded-[20px] ring-2 ring-primary ring-offset-2 ring-offset-card',
              )}
              style={{ width }}
            >
              {editing && form.showQr && (
                <div
                  role="button"
                  tabIndex={0}
                  aria-label="Drag to reposition the permit QR"
                  onPointerDown={startDrag}
                  className="absolute z-10 cursor-grab rounded-md ring-2 ring-primary active:cursor-grabbing"
                  style={qrHandleStyle(
                    draft ??
                      bannerQrDefault(ratio, form.qrSize, 'social', galleryCount),
                    qrGeo,
                  )}
                />
              )}
              {isLookbook ? (
                <ListingLookbook
                  width={width}
                  brand={{ ...LOOKBOOK_DEFAULT_BRAND, ...brand }}
                  listing={{ ...bannerListingFrom(listing), counter: null }}
                />
              ) : (
                <ListingBanner
                  width={width}
                  ratio={ratio}
                  variant="social"
                  brand={brand}
                  listing={bannerListingFrom(listing)}
                />
              )}
            </div>
          </div>
        )}
      </div>
    </Card>
  );
}
